import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material';
import { BRAND } from '../../configuration/theme';

/**
 * Surfaces for the M.E.-Kollektiv layout.
 *
 * The CI is calm and flat: solid CI colours, generous whitespace, thin rules.
 * Nothing here blurs, glows or moves, both because that is the identity and
 * because visual noise is a real accessibility cost for this audience.
 */

export type SectionTone = 'default' | 'offWhite' | 'light' | 'brand';

/**
 * Background for a full-width page section.
 *
 * The site runs on one background from the header to the footer: no tinted
 * bands, no CI-blue panel to close a page with. What separates one section
 * from the next is the space between them, and nothing else. That is an
 * accessibility decision as much as a visual one - a page that changes colour
 * four times as you scroll is four more things to process for a reader who has
 * a limited amount of processing to spend.
 *
 * The tones below are therefore no longer used by anything the site ships with.
 * They stay because the editing interface still offers them for the one section
 * that genuinely has to stand apart, and because taking a field away from the
 * editing interface deletes it out of the content files on the next save.
 */
export const getSectionStyles = (theme: Theme, tone: SectionTone = 'default'): SxProps => {
  const isDark = theme.palette.mode === 'dark';

  switch (tone) {
    case 'offWhite':
      return {
        backgroundColor: isDark ? theme.palette.background.paper : BRAND.offWhite,
        color: theme.palette.text.primary,
      };
    case 'light':
      return {
        backgroundColor: isDark ? '#2B323E' : BRAND.light,
        color: isDark ? theme.palette.text.primary : BRAND.anthracite,
      };
    case 'brand':
      return {
        backgroundColor: isDark ? BRAND.dark : BRAND.primary,
        color: '#FFFFFF',
      };
    default:
      return {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      };
  }
};

/**
 * The CI graphics frame their tinted panels with a thin inset white rule.
 * Applied to a section, this reproduces that frame.
 */
export const getFramedPanelStyles = (theme: Theme): SxProps => ({
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(227,230,236,0.25)' : 'rgba(255,255,255,0.85)'}`,
  borderRadius: 2,
  p: { xs: 3, sm: 5, md: 6 },
});

export type CardVariant = 'outlined' | 'tinted' | 'contrast' | 'tile';

/** Card surface. `outlined` is the default; `tinted` sits on a plain section. */
export const getCardStyles = (theme: Theme, variant: CardVariant = 'outlined'): SxProps => {
  const isDark = theme.palette.mode === 'dark';

  const base: SxProps = {
    borderRadius: 3,
    height: '100%',
    p: { xs: 3, md: 4 },
    transition: 'border-color 160ms ease, background-color 160ms ease',
  };

  switch (variant) {
    case 'tinted':
      return {
        ...base,
        backgroundColor: isDark ? 'rgba(167,183,214,0.08)' : 'rgba(93,116,171,0.08)',
        border: `1px solid ${isDark ? 'rgba(167,183,214,0.18)' : 'rgba(93,116,171,0.22)'}`,
      };
    case 'contrast':
      return {
        ...base,
        backgroundColor: isDark ? 'rgba(27,31,39,0.55)' : 'rgba(255,255,255,0.9)',
        border: `1px solid ${isDark ? 'rgba(227,230,236,0.2)' : 'rgba(255,255,255,0.9)'}`,
      };
    /*
     * The library's tile, for a set that is read rather than pressed: the same
     * blue edge on the page's own background, so a shelf of fields on one page
     * and a shelf of topics on the other read as the same kind of thing.
     *
     * No fill under the pointer, which is the one thing it does not borrow. A
     * fill following the cursor is what says "this opens"; on a tile that only
     * carries its text it would promise a click the surface does not take.
     */
    case 'tile':
      return {
        ...base,
        backgroundColor: 'transparent',
        border: `1.5px solid ${topicTileStates(theme).border}`,
      };
    default:
      return {
        ...base,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': {
          borderColor: isDark ? BRAND.light : BRAND.primary,
        },
      };
  }
};

/**
 * Vertical rhythm for page sections.
 *
 * Padding, so two neighbouring sections add up: at 11 that came to about 175px
 * of nothing between one section and the next on a laptop, which was set for a
 * site whose sections changed colour - the space had to do the separating on
 * its own where two tinted bands met. On one background it reads as a gap
 * rather than as a rhythm, and it costs a reader with limited energy real
 * scrolling to get past. 7 leaves about 112px, still generous.
 */
export const sectionSpacing = { py: { xs: 5, md: 7 } } as const;

/**
 * For a section that continues the one above it rather than starting something
 * new: a button under the page's own opening, and nothing else.
 *
 * Given the full rhythm it read as a stranded button in a field of white -
 * separated from the line that explains it by more space than that line has to
 * the heading above. The rhythm exists to separate *sections*; a control that
 * belongs to the text above it is not one.
 */
export const tightSectionSpacing = { py: { xs: 1.5, md: 2 } } as const;

/**
 * How wide a column of running text may get, whatever it is sitting in.
 *
 * Two different jobs used to be done by one number here, and they pulled apart
 * on a wide screen. A section's container decides where the page's left edge
 * is, and every section wants the same answer or the edge visibly steps in and
 * out as you scroll. How long a line of text may run is a separate question
 * with a separate answer - somewhere around seventy characters, past which the
 * eye starts losing which line comes next on the way back to the left margin.
 * Sections carrying prose were being narrowed to `md` to buy the second, which
 * cost the first, and the paragraphs inside them still needed capping on top:
 * there were six different hand-set widths doing it, none of them agreeing.
 *
 * So the container is `lg` throughout and the measure lives here.
 *
 * In pixels, and not in `ch`, which is the obvious unit for the job and the
 * wrong one here. `ch` is the width of the digit zero, and how that relates to
 * the width of average running text is a property of the typeface rather than
 * a constant: in Montserrat it is far narrower than the rule of thumb assumes,
 * and a measure set at the count of characters that is right on paper came out
 * around two thirds of the width it should have been.
 *
 * The numbers themselves are set wider than the readability rule of thumb for
 * running text, which asks for something closer to 700px at this size. That is
 * a deliberate call: on a desktop window the narrower column read as a phone
 * layout parked in the middle of the screen, and looking like the site was
 * never designed for the machine it is being read on was judged the worse of
 * the two costs. The line height carries some of it back - 1.8 is generous,
 * and a long line is easier to track when the next one is well clear of it.
 *
 * Forms are the exception, and stay narrow on their own: a field the width of
 * the page is harder to fill in, not easier.
 */
export const textMeasure = { maxWidth: 1280 } as const;

/**
 * The same idea for headings, and a narrower number than the text it sits over,
 * which looks like a mistake and is not. Given the full width a headline at
 * this size runs almost edge to edge and comes out visibly wider than the
 * paragraph beneath it, so the block reads as top-heavy - and it breaks
 * wherever it happens to run out of room, which on the start page left "M.E."
 * standing alone on a second line. Held in, it turns two lines of roughly equal
 * length and sits inside the column of text rather than over it.
 *
 * `textWrap: balance` does the rest: the browser evens out the lines instead of
 * filling the first and leaving the remainder, which is what strands a short
 * last word. It applies to headings only - browsers cap it at a handful of
 * lines, and it is the wrong effect on a paragraph anyway.
 */
export const displayMeasure = { maxWidth: 1100, textWrap: 'balance' } as const;

/**
 * The band a page opens with sits tighter than a content section.
 *
 * It carries the h1 and a line of orientation, nothing a reader stays with - so
 * at a section's rhythm it became a field to scroll past before the page began.
 *
 * Uneven on purpose. Above the title the room is the wash's: it is what lets
 * the colour be seen at all before the text starts, and it separates the page
 * from the header sitting on top of it. Below there is no such job, and the
 * section that follows brings its own opening padding - together they came to
 * something like a screen's worth of nothing between the title of a page and
 * its first heading. So the band closes tight and lets the section below
 * provide the distance.
 */
export const pageHeaderSpacing = { pt: { xs: 3.5, md: 5 }, pb: { xs: 2, md: 2.5 } } as const;

/**
 * The start page's hero: taller than a page header, because it does open the
 * site - but nowhere near a full section either. At a section's rhythm it ran
 * past the fold on a laptop, so the first thing a visitor saw was a headline
 * with nothing under it.
 */
export const heroSpacing = { py: { xs: 3, md: 6 } } as const;

/** Accent colour that is safe for body-sized text on the current background. */
export const accentTextColor = (theme: Theme): string =>
  theme.palette.mode === 'dark' ? BRAND.accentTextDark : BRAND.accentText;

/**
 * The three states of a compact topic tile, in the CI blue.
 *
 * Closed it is an outline, under the pointer it fills, and open it stays
 * filled a step stronger - so the shelf shows at a glance which tile the
 * answer on screen belongs to.
 *
 * A wash of the blue rather than the blue itself. Filling the open tile with
 * CI blue would put its text in white on a mid-tone, and the open tile is the
 * one that carries something to read - three or four sentences and a link.
 * The wash keeps every one of those at the contrast the rest of the page has.
 */
export const topicTileStates = (theme: Theme) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    border: isDark ? BRAND.light : BRAND.primary,
    hover: isDark ? 'rgba(167,183,214,0.12)' : 'rgba(93,116,171,0.12)',
    open: isDark ? 'rgba(167,183,214,0.18)' : 'rgba(93,116,171,0.18)',
  };
};
